import React, { useState, useEffect } from 'react';
import { Card, Button, Spin, message, Statistic, Row, Col } from 'antd';
import { ReloadOutlined, DownloadOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import apiClient from '../../../utils/apiClient';
import { downloadCSV } from '../../../utils/csvExport';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface CouponStats {
  transactionCount: number;
  avgBeforeDiscount: number;
  avgAfterDiscount: number;
  avgDiscount: number;
  totalRevenue: number;
}

interface RegularStats {
  transactionCount: number;
  avgPurchase: number;
  totalRevenue: number;
}

interface ComparisonData {
  coupon: CouponStats;
  regular: RegularStats;
  comparison: {
    upliftPercentage: number;
    couponAvgHigher: boolean;
  };
}

const CouponVsRegularComparison: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ComparisonData | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/coupon-analytics/analytics/coupon-vs-regular');

      if (response.data.success) {
        setData(response.data.data);
      } else {
        message.error('データ取得に失敗しました');
      }
    } catch (error) {
      console.error('クーポンvs通常購入比較エラー:', error);
      message.error('データ取得エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleExportCSV = () => {
    if (!data) return;

    const csvData = [
      {
        '種類': 'クーポン使用',
        '取引数': data.coupon.transactionCount,
        '平均購入額': `¥${data.coupon.avgAfterDiscount.toLocaleString()}`,
        '平均値引き額': `¥${data.coupon.avgDiscount.toLocaleString()}`,
        '総売上': `¥${data.coupon.totalRevenue.toLocaleString()}`
      },
      {
        '種類': '通常購入',
        '取引数': data.regular.transactionCount,
        '平均購入額': `¥${data.regular.avgPurchase.toLocaleString()}`,
        '平均値引き額': '-',
        '総売上': `¥${data.regular.totalRevenue.toLocaleString()}`
      },
      {
        '種類': '比較',
        '取引数': '-',
        '平均購入額': `${data.comparison.upliftPercentage > 0 ? '+' : ''}${data.comparison.upliftPercentage}%`,
        '平均値引き額': '-',
        '総売上': '-'
      }
    ];

    downloadCSV(csvData, 'クーポンvs通常購入比較.csv');
    message.success('CSVファイルをダウンロードしました');
  };

  // 比較グラフデータ
  const comparisonChartData = data ? {
    labels: ['クーポン使用', '通常購入'],
    datasets: [
      {
        label: '平均購入額（円）',
        data: [data.coupon.avgAfterDiscount, data.regular.avgPurchase],
        backgroundColor: ['rgba(75, 192, 192, 0.6)', 'rgba(153, 102, 255, 0.6)'],
        borderColor: ['rgba(75, 192, 192, 1)', 'rgba(153, 102, 255, 1)'],
        borderWidth: 1
      }
    ]
  } : { labels: [], datasets: [] };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: '平均購入額の比較',
        font: {
          size: 16
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: '金額（円）'
        }
      }
    }
  };

  return (
    <div>
      {/* アクションボタン */}
      <Card className="mb-4">
        <div className="flex justify-end gap-2">
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchData}
            loading={loading}
          >
            更新
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportCSV}
            disabled={!data}
          >
            CSV出力
          </Button>
        </div>
      </Card>

      {/* 説明カード */}
      <Card className="mb-4 bg-blue-50">
        <div className="flex items-start gap-3">
          <div className="text-2xl">💡</div>
          <div>
            <div className="font-bold text-gray-800 mb-1">クーポン使用vs通常購入比較とは？</div>
            <div className="text-sm text-gray-600">
              クーポンを使用した取引と、クーポンを使用しない通常購入を比較します。
              クーポンが客単価の向上に貢献しているかを評価できます。
            </div>
          </div>
        </div>
      </Card>

      {loading ? (
        <Card>
          <div className="flex justify-center items-center" style={{ minHeight: 400 }}>
            <Spin size="large" />
          </div>
        </Card>
      ) : data ? (
        <>
          {/* 比較サマリー */}
          <Card className="mb-4" title="📊 クーポン効果サマリー">
            <Row gutter={16}>
              <Col span={12}>
                <Card className="bg-green-50">
                  <div className="text-center">
                    <div className="text-lg font-bold text-gray-700 mb-2">🎫 クーポン使用時</div>
                    <Statistic
                      title="平均購入額"
                      value={data.coupon.avgAfterDiscount}
                      prefix="¥"
                      valueStyle={{ color: '#3f8600', fontSize: '2rem' }}
                    />
                    <div className="mt-4 text-sm text-gray-600">
                      <div>取引数: {data.coupon.transactionCount}件</div>
                      <div>平均値引き: ¥{data.coupon.avgDiscount.toLocaleString()}</div>
                    </div>
                  </div>
                </Card>
              </Col>
              <Col span={12}>
                <Card className="bg-purple-50">
                  <div className="text-center">
                    <div className="text-lg font-bold text-gray-700 mb-2">💰 通常購入時</div>
                    <Statistic
                      title="平均購入額"
                      value={data.regular.avgPurchase}
                      prefix="¥"
                      valueStyle={{ color: '#9254de', fontSize: '2rem' }}
                    />
                    <div className="mt-4 text-sm text-gray-600">
                      <div>取引数: {data.regular.transactionCount}件</div>
                      <div>値引き: なし</div>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>
          </Card>

          {/* アップリフト統計 */}
          <Row gutter={16} className="mb-4">
            <Col span={8}>
              <Card>
                <Statistic
                  title="客単価アップリフト"
                  value={Math.abs(data.comparison.upliftPercentage)}
                  precision={2}
                  suffix="%"
                  prefix={data.comparison.couponAvgHigher ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                  valueStyle={{
                    color: data.comparison.couponAvgHigher ? '#3f8600' : '#cf1322'
                  }}
                />
                <div className="mt-2 text-xs text-gray-500">
                  {data.comparison.couponAvgHigher
                    ? 'クーポン使用で客単価が向上'
                    : 'クーポン使用で客単価が減少'}
                </div>
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="クーポン総売上"
                  value={data.coupon.totalRevenue}
                  prefix="¥"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="通常購入総売上"
                  value={data.regular.totalRevenue}
                  prefix="¥"
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>

          {/* 比較グラフ */}
          <Card title="平均購入額の比較（グラフ）">
            <Bar data={comparisonChartData} options={chartOptions} />
          </Card>

          {/* 解釈ガイド */}
          <Card className="mt-4 bg-yellow-50">
            <div className="flex items-start gap-3">
              <div className="text-2xl">📈</div>
              <div>
                <div className="font-bold text-gray-800 mb-2">結果の解釈</div>
                <div className="text-sm text-gray-700 space-y-2">
                  {data.comparison.couponAvgHigher ? (
                    <>
                      <div>✅ クーポン使用時の客単価が通常購入より <strong>{data.comparison.upliftPercentage.toFixed(2)}%高い</strong> です。</div>
                      <div>✅ クーポンは客単価向上に<strong>貢献している</strong>と言えます。</div>
                      <div>💡 クーポンが購買意欲を刺激し、追加購入を促進している可能性があります。</div>
                    </>
                  ) : (
                    <>
                      <div>⚠️ クーポン使用時の客単価が通常購入より <strong>{Math.abs(data.comparison.upliftPercentage).toFixed(2)}%低い</strong> です。</div>
                      <div>⚠️ クーポンが客単価<strong>減少</strong>に繋がっている可能性があります。</div>
                      <div>💡 クーポン設計を見直し、最低購入金額を設定するなどの対策を検討してください。</div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </>
      ) : (
        <Card>
          <div className="text-center text-gray-500 py-12">
            「更新」ボタンをクリックしてデータを読み込んでください
          </div>
        </Card>
      )}
    </div>
  );
};

export default CouponVsRegularComparison;
